import { userExamMetadataModule } from '@/store/userExamMetadata/module'
import { userModule } from '@/store/user/module'
import { isPromise } from '@/store/utils'
import type { TSubjectWithLevels } from '@/store/examMetadata/state'
import { difference } from '@/utils'
import { subscriptionModule } from '@/store/subscription/module'
import { examMetadataModule } from '@/store/examMetadata/module'

const getUserExamMetadata = () => {
    const userExamMetadata = userExamMetadataModule.state.userExamMetadata.value
    if (isPromise(userExamMetadata)) {
        return []
    } else {
        return userExamMetadata
    }
}

const getUserExamMetadataForObjectId = (objectId: string) => {
    return getUserExamMetadata().find(uem => uem.objectId === objectId)
}

const getUserExamMetadataForExamGuid = (examGuid: string) => {
    // If examGuid is user's current examGuid, return their currentUserExamMetadata
    const currentUEM = getCurrentUserExamMetadata()
    if (examGuid === currentUEM?.examGuid) {
        return currentUEM
    }

    return getUserExamMetadata().find(uem => uem.examGuid === examGuid)
}

const getCurrentUserExamMetadata = () => {
    const uems = userExamMetadataModule.getters.getUserExamMetadata()

    const currentUEMPointer = userModule.state.user?.currentUserExamMetadata
    const currentUEMFromPointer = uems.find(uem => uem.objectId === currentUEMPointer?.objectId) || null
    if (currentUEMFromPointer) {
        return currentUEMFromPointer
    }

    // Also still check currentExamGuid for now, for legacy UEM records
    const examGuid = userModule.state.user?.currentExamGuid
    const currentUEMFromExamGuid = examGuid && uems.find(uem => uem.examGuid === examGuid)

    return currentUEMFromExamGuid || null
}

const getFlaggedQuestions = () => {
    const uem = userExamMetadataModule.getters.getCurrentUserExamMetadata()
    return uem?.flaggedQuestions || []
}

const getLevelUpProgress = () => {
    const uem = userExamMetadataModule.getters.getCurrentUserExamMetadata()
    return uem?.levelUpProgress || []
}

const getMessageReferralConfig = () => {
    const uem = userExamMetadataModule.getters.getCurrentUserExamMetadata()
    return uem?.messageConfig?.referral
}

const getUemDisabledSubjects = (examGuid: string | undefined, subjects: TSubjectWithLevels[]) => {
    if (!examGuid || !subjects) {
        return []
    }

    const uemForExamGuid = userExamMetadataModule.getters.getUserExamMetadataForExamGuid(examGuid)
    const disabledSubjectIds = uemForExamGuid?.disabledSubjectIds
    const disabledSubjectNames = uemForExamGuid?.disabledSubjects

    // Start by checking disabledSubjectIds
    // There may be a mismatch between the number of disabledSubjectIds and disabledSubjectNames
    // Return disabledSubjectIds plus any extra disabledSubjects from disabledSubjectNames
    if (disabledSubjectIds?.length) {
        if (disabledSubjectNames?.length) {
            const disabledSubjectNameIds = subjects.reduce<string[]>((acc, subject) => {
                if (disabledSubjectNames?.includes(subject.subjectName)) {
                    acc.push(subject.subjectId)
                }
                return acc
            }, [])
            return [ ...disabledSubjectIds, ...difference(disabledSubjectNameIds, disabledSubjectIds) ]
        }
        return disabledSubjectIds
    }

    // If the user does not have disabledSubjectIds on their uem record,
    // then return the converted disabledSubjectIds array from the disabledSubjectNames
    if (disabledSubjectNames?.length) {
        return subjects.reduce<string[]>((acc, subject) => {
            if (disabledSubjectNames?.includes(subject.subjectName)) {
                acc.push(subject.subjectId)
            }
            return acc
        }, [])
    }

    return []
}

const getLastLevelUpResetDate = () => {
    const sortedResetDates = userExamMetadataModule.getters.getCurrentUserExamMetadata()?.levelUpResetDates
        ?.sort((a, b) => new Date(b.iso).getTime() - new Date(a.iso).getTime())
    const mostRecentResetDate = sortedResetDates?.[0]
    return mostRecentResetDate ? new Date(mostRecentResetDate.iso) : null
}

const getScheduledExamDate = () => {
    const currentUEM = getCurrentUserExamMetadata()
    const scheduledExamDate = currentUEM?.scheduledExamDate
    if (!scheduledExamDate?.iso) {
        return null
    }

    return new Date(scheduledExamDate.iso)
}

const getDaysUntilExam = () => {
    const scheduledExamDate = getScheduledExamDate()
    if (!scheduledExamDate) {
        return null
    }
    const scheduledTime = scheduledExamDate.getTime()
    const daysUntilExam = Math.ceil((scheduledTime - new Date().getTime()) / 86400000)
    return daysUntilExam
}

/**
 * Should only see exam date banner if:
 * - no exam date already set
 * - only premium user should see banner on the Stats tab
 * - current subscription (or User) was created >72 hours ago
 * - has not previously dismissed the banner
 */
const getCanSeeExamDateBanner = (tab: 'Study' | 'Stats') => {
    // We can only do the following checks for users with a valid User record
    // And we have a current exam
    const currentUser = userModule.state.user
    const currentExam = examMetadataModule.getters.getCurrentExamMetadata()
    if (!currentUser || !currentExam) {
        return false
    }

    // Check if user has already scheduled their exam date
    const hasScheduledExamDate = !!getScheduledExamDate()
    if (hasScheduledExamDate) {
        return false
    }

    // We want to show Exam Date Banner to Premium and Basic users on Study Tab
    // ONLY user with premium access should see Exam Date Banner on Stats Tab
    // Check if user currently has premium access
    // getSubscriptionForExamId returns true for free exams, subscription for subscribers + licensed users, else false
    const currentSubscription = subscriptionModule.getters.getSubscriptionForExamId()
    const isPremiumUser = !!currentSubscription
    if (tab === 'Stats' && !isPremiumUser) {
        return false
    }

    // Check if user is beyond 72 hour window
    // For subscribers, use subscription created date. For others (like studying a free exam), use User created date
    const subscriptionCreatedAt = typeof currentSubscription === 'object'
        ? new Date(currentSubscription.createdAt)
        : null
    const userCreatedAt = new Date(currentUser.createdAt)
    const bannerReferenceDate = subscriptionCreatedAt || userCreatedAt
    const seventyTwoHoursAgo = Date.now() - (72 * 60 * 60 * 1000)
    const isPast72Hours = bannerReferenceDate.getTime() <= seventyTwoHoursAgo
    if (!isPast72Hours) {
        return false
    }

    // Check if user has dismissed the banner in the past
    const hasSeenExamDateBanner = userModule.getters.getHasSeenMessage('examDateBanner', currentExam)
    if (hasSeenExamDateBanner) {
        return false
    }

    // If user passes all the above, then they can see the banner
    return true
}

const getCanTrialLevelUp = () => {
    const hasActiveSubscription = subscriptionModule.getters.getSubscriptionForExamId()
    const hasUsedLevelUpTrial = userModule.state.user?.crossPlatformConfig?.levelUpFreeTrial?.hasUsedLevelUpTrial
    return !hasActiveSubscription && !hasUsedLevelUpTrial
}

export default {
    getUserExamMetadata,
    getUserExamMetadataForObjectId,
    getUserExamMetadataForExamGuid,
    getCurrentUserExamMetadata,
    getFlaggedQuestions,
    getLevelUpProgress,
    getMessageReferralConfig,
    getUemDisabledSubjects,
    getLastLevelUpResetDate,
    getScheduledExamDate,
    getDaysUntilExam,
    getCanSeeExamDateBanner,
    getCanTrialLevelUp,
}
